/**
 * AttendanceAdjustmentDialog — Dialog për korrigjim manual të orëve
 * Admin/menaxheri korrigjon check-in, check-out, pauzë për një ditë specifike.
 * Ruan audit history me arsye + kush bëri ndryshimin.
 */
import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Stack, Typography, Divider, Box,
  Chip, CircularProgress, Alert, Table, TableHead, TableRow,
  TableCell, TableBody, TableContainer, IconButton, Tooltip,
  InputAdornment,
} from '@mui/material';
import EditIcon         from '@mui/icons-material/Edit';
import HistoryIcon      from '@mui/icons-material/History';
import SaveIcon         from '@mui/icons-material/Save';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  getAdjustmentHistory,
  saveAdjustment,
} from '../../services/attendanceDemoService';

import { getAuthHeaders, getLogedInUser } from '../../services/authSession';
import { API_URL, isDemoMode } from '../../services/backendMode';


function toTimeInput(isoOrNull) {
  if (!isoOrNull) return '';
  const d = new Date(isoOrNull);
  if (isNaN(d)) return isoOrNull.slice(0, 5) || '';
  return d.toTimeString().slice(0, 5);
}

function fmtTime(isoOrNull) {
  if (!isoOrNull) return '—';
  const d = new Date(isoOrNull);
  return isNaN(d) ? (isoOrNull.slice(0, 5) || '—') : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit' });
}

function fmtMin(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  if (!m) return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('sq-AL', { dateStyle: 'short', timeStyle: 'short' });
}

// Build a full ISO datetime for a given date + HH:mm string
function buildIso(dateStr, timeStr) {
  if (!dateStr || !timeStr) return null;
  return `${dateStr}T${timeStr}:00`;
}

function getCurrentUserName() {
  try {
    const u = getLogedInUser();
    const d = u?.data || u;
    // loginService ruan `firstname`/`lastname`; disa përgjigje të API-t përdorin
    // `first_name`/`last_name` — prano të dyja që emri të mos dalë bosh.
    const first = d?.firstname || d?.first_name;
    const last  = d?.lastname  || d?.last_name;
    if (first) return `${first} ${last || ''}`.trim();
    return d?.email || null;
  } catch { return null; }
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AttendanceAdjustmentDialog({ open, onClose, row, workerName, onSaved, readOnly = false }) {
  // row shape: { date, day_name, session_id, check_in, check_out, break_minutes,
  //             work_minutes, net_worked_minutes, planned_minutes,
  //             is_adjusted, adj_reason, adj_by_name, adj_at, worker_id }

  const [ciInput,  setCiInput]  = useState('');
  const [coInput,  setCoInput]  = useState('');
  const [brInput,  setBrInput]  = useState('');
  const [reason,   setReason]   = useState('');
  const [saving,   setSaving]   = useState(false);
  const [error,    setError]    = useState('');
  const [history,  setHistory]  = useState([]);
  const [loadHist, setLoadHist] = useState(false);
  const [showHist, setShowHist] = useState(false);

  // Init form from row
  useEffect(() => {
    if (!open || !row) return;
    setCiInput(toTimeInput(row.check_in));
    setCoInput(toTimeInput(row.check_out));
    setBrInput(row.break_minutes != null ? String(row.break_minutes) : '');
    // Read-only viewers (workers) see the existing correction reason instead of a blank field.
    setReason(readOnly ? (row.adj_reason || '') : '');
    setError('');
    setShowHist(false);
    setHistory([]);
  }, [open, row, readOnly]);

  // Load adjustment history when toggled
  async function loadHistory() {
    if (!row) return;
    setLoadHist(true);
    try {
      if (isDemoMode()) {
        setHistory(getAdjustmentHistory(row.worker_id, row.date));
      } else {
        const qs = row.session_id
          ? `session_id=${row.session_id}`
          : `worker_id=${row.worker_id}&date=${row.date}`;
        const res = await fetch(`${API_URL}/api/attendance/adjustments?${qs}`, {
          headers: getAuthHeaders(),
        });
        const j = await res.json().catch(() => ({}));
        setHistory(j?.data || []);
      }
    } catch {
      setHistory([]);
    } finally {
      setLoadHist(false);
    }
  }

  function toggleHistory() {
    if (!showHist) loadHistory();
    setShowHist(v => !v);
  }

  async function handleSave() {
    if (!reason.trim()) { setError('Arsyeja është e detyrueshme.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        session_id:         row.session_id || null,
        worker_id:          row.worker_id,
        adj_date:           row.date,
        adj_check_in:       buildIso(row.date, ciInput) || null,
        adj_check_out:      buildIso(row.date, coInput) || null,
        adj_break_minutes:  brInput !== '' ? Number(brInput) : null,
        adj_work_minutes:   null, // computed from check_in/out
        reason:             reason.trim(),
      };

      if (isDemoMode()) {
        const r = saveAdjustment(payload);
        if (!r.success) { setError(r.error || 'Gabim!'); setSaving(false); return; }
      } else {
        const res = await fetch(`${API_URL}/api/attendance/adjustments`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body:    JSON.stringify(payload),
        });
        const j = await res.json().catch(() => ({}));
        if (!j.success) { setError(j.error || 'Gabim nga serveri.'); setSaving(false); return; }
      }

      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setError('Gabim në lidhje me serverin.');
    } finally {
      setSaving(false);
    }
  }

  if (!row) return null;

  // Compute preview of adjusted worked hours
  const previewWorked = (() => {
    if (!ciInput || !coInput) return null;
    const ci = new Date(`${row.date}T${ciInput}:00`);
    const co = new Date(`${row.date}T${coInput}:00`);
    if (isNaN(ci) || isNaN(co) || co <= ci) return null;
    const total = Math.round((co - ci) / 60000);
    const brk   = brInput !== '' ? Number(brInput) : (row.break_minutes || 0);
    return Math.max(0, total - brk);
  })();

  const diffPreview = previewWorked != null && row.planned_minutes != null
    ? previewWorked - row.planned_minutes
    : null;

  const deltaFromOrig = previewWorked != null && row.net_worked_minutes != null
    ? previewWorked - row.net_worked_minutes
    : null;

  const currentUserName = getCurrentUserName();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6" fontWeight={700}>
              {readOnly
                ? <InfoOutlinedIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />
                : <EditIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 20 }} />}
              {readOnly ? (row.is_adjusted ? 'Detajet e korrigjimit' : 'Detajet e ditës') : 'Korrigjim manual'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {workerName} — {row.date} ({row.day_name})
            </Typography>
          </Stack>
          <Tooltip title="Historiku i korrigjimeve">
            <IconButton onClick={toggleHistory} size="small" color={showHist ? 'primary' : 'default'}>
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Original values banner */}
        <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 1.5, mb: 2 }}>
          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
            <InfoOutlinedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
            Vlerat aktuale (para korrigjimit)
          </Typography>
          <Stack direction="row" spacing={3}>
            <Box><Typography variant="caption" color="text.secondary">Hyrja</Typography><br />
              <Typography variant="body2" fontWeight={600}>{fmtTime(row.check_in_raw ?? row.check_in)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Dalja</Typography><br />
              <Typography variant="body2" fontWeight={600}>{fmtTime(row.check_out_raw ?? row.check_out)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Pauza</Typography><br />
              <Typography variant="body2" fontWeight={600}>{fmtMin(row.break_minutes_raw ?? row.break_minutes)}</Typography></Box>
            <Box><Typography variant="caption" color="text.secondary">Orë neto</Typography><br />
              <Typography variant="body2" fontWeight={600}>{fmtMin(row.net_worked_minutes)}</Typography></Box>
          </Stack>
        </Box>

        {/* Edit form */}
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Hyrja (e korrigjuar)"
              type="time"
              size="small"
              value={ciInput}
              onChange={e => setCiInput(e.target.value)}
              disabled={readOnly}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
            <TextField
              label="Dalja (e korrigjuar)"
              type="time"
              size="small"
              value={coInput}
              onChange={e => setCoInput(e.target.value)}
              disabled={readOnly}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ step: 60 }}
            />
          </Stack>

          <TextField
            label="Pauza (minuta)"
            type="number"
            size="small"
            value={brInput}
            onChange={e => setBrInput(e.target.value)}
            disabled={readOnly}
            inputProps={{ min: 0, max: 240 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">min</InputAdornment>,
            }}
            sx={{ width: '50%' }}
          />

          {/* Live preview */}
          {previewWorked != null && (
            <Box sx={{ bgcolor: 'primary.50', border: '1px solid', borderColor: 'primary.200',
              borderRadius: 2, p: 1.5 }}>
              <Typography variant="caption" color="primary.main" fontWeight={600}>
                Pas korrigjimit:
              </Typography>
              <Stack direction="row" spacing={3} mt={0.5}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Orë neto</Typography><br />
                  <Typography variant="body2" fontWeight={700} color="primary.main">{fmtMin(previewWorked)}</Typography>
                </Box>
                {diffPreview != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Diferenca</Typography><br />
                    <Typography variant="body2" fontWeight={700}
                      color={diffPreview >= 0 ? 'success.main' : 'error.main'}>
                      {diffPreview >= 0 ? '+' : ''}{fmtMin(Math.abs(diffPreview))}
                      {diffPreview >= 0 ? ' shtesë' : ' mungesë'}
                    </Typography>
                  </Box>
                )}
                {row.planned_minutes != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Planifikuar</Typography><br />
                    <Typography variant="body2" color="text.secondary">{fmtMin(row.planned_minutes)}</Typography>
                  </Box>
                )}
                {deltaFromOrig != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Ndryshim nga origjinali</Typography><br />
                    <Typography variant="body2" fontWeight={700}
                      color={deltaFromOrig > 0 ? 'success.main' : deltaFromOrig < 0 ? 'error.main' : 'text.secondary'}>
                      {deltaFromOrig === 0 ? 'Pa ndryshim' : (deltaFromOrig > 0 ? '+' : '') + fmtMin(Math.abs(deltaFromOrig)) + (deltaFromOrig > 0 ? ' shtohet' : ' hiqet')}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          )}

          <TextField
            label={readOnly ? 'Arsyeja e korrigjimit' : 'Arsyeja e korrigjimit *'}
            multiline
            rows={2}
            size="small"
            value={reason}
            onChange={e => setReason(e.target.value)}
            disabled={readOnly}
            placeholder="p.sh. Sistemi regjistroi gabimisht orën e hyrjes…"
            error={!!error && !reason.trim()}
            fullWidth
          />

          {error && <Alert severity="error" sx={{ py: 0.5 }}>{error}</Alert>}

          {readOnly ? (
            (row.adj_by_name || row.adj_at) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">
                  Korrigjuar nga:
                </Typography>
                <Typography variant="caption" fontWeight={700} color="text.primary">
                  {row.adj_by_name || '—'}
                </Typography>
                {row.adj_at && (
                  <Typography variant="caption" color="text.secondary">
                    · {fmtDateTime(row.adj_at)}
                  </Typography>
                )}
              </Box>
            )
          ) : currentUserName && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                Ky korrigjim do të shënohet si i bërë nga:
              </Typography>
              <Typography variant="caption" fontWeight={700} color="text.primary">
                {currentUserName}
              </Typography>
            </Box>
          )}
        </Stack>

        {/* Audit history */}
        {showHist && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              <HistoryIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
              Historiku i korrigjimeve
            </Typography>
            {loadHist ? (
              <Box display="flex" justifyContent="center" py={2}>
                <CircularProgress size={24} />
              </Box>
            ) : history.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nuk ka korrigjime të mëparshme.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 220 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Nga</TableCell>
                      <TableCell>Hyrja → Dalja</TableCell>
                      <TableCell>Pauza</TableCell>
                      <TableCell>Arsyeja</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {history.map((h, i) => (
                      <TableRow key={h.id || i}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption">{fmtDateTime(h.adjusted_at)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{h.adjusted_by_name || '—'}</Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption">
                            {fmtTime(h.adj_check_in)} → {fmtTime(h.adj_check_out)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{fmtMin(h.adj_break_minutes)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{h.reason}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">{readOnly ? 'Mbyll' : 'Anulo'}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || !reason.trim()}
            sx={{ borderRadius: 2 }}
          >
            Ruaj korrigjimin
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
